import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import config from 'config';
import type { GetStoreItemsSub } from 'src/openapi';
import {
  PRO_AI_CREDITS_EXPIRY_NOTE,
  type ProUnlocks,
} from 'src/components/buyCreditsDialog/benefits';
import { ctaProps } from 'src/helpers/ctaTracking';
import {
  type FunnelEventType,
  logProPitchEvent,
  newInstanceId,
} from 'src/helpers/experimentFunnel';
import { proRecurringTerms } from 'src/helpers/subscription';
import { useProTierOptions } from 'src/helpers/hooks/useProTerms';
import { useAppSelector } from 'src/redux';
import useSeen from 'src/components/personalProCard/useSeen';
import {
  CloseButton,
  CtaFooter,
  DiscountPill,
  FootNote,
  GoProCta,
  HeadBox,
  PitchTitle,
  PriceMain,
  PriceRow,
  PriceStrike,
  PriceTerm,
  ProChip,
  Shell,
  TopUpLink,
  termsLinkSx,
} from './sheet.styles';
import type { ProNeed } from './needs';
import { useProPitchCopy } from './variants';
import OfferCountdown, {
  TodayOnlyPrice,
  offerEndsAt,
  useLiveOffer,
} from './OfferCountdown';
import ProCompare from './compare';
import TierPicker from './TierPicker';
import { type ProTierType, proTierOf } from './tiers';
import { PitchBody, ShortNote } from './styles';

export interface CareerProPitchLink {
  label: string;
  onClick: () => void;
  /** Tracked as a CTA under this name (see ctaProps). */
  cta: string;
}

export interface CareerProPitchProps {
  /** Where the pitch opened, for CTA tracking: every control on it is tracked
   * under this placement, and Go Pro also under the need it led with, so a
   * conversion can be read per surface and per question. */
  placement: string;
  premium?: GetStoreItemsSub;
  /** The store is still loading, so there's no price to show yet. */
  loading?: boolean;
  /** A purchase is being started. */
  busy?: boolean;
  ctaLabel?: string;
  /** What the candidate was trying to do, and why it stopped -- e.g. a short
   * AI-credit balance. Shown above the headline. */
  note?: React.ReactNode;
  /** The need closest to what the candidate was doing; the table leads with it. */
  lead?: ProNeed;
  /** The course in front of them, named under the courses row. */
  courseName?: string;
  /** Counts the opener knows better than the store's (see BuyCreditsDialog). */
  proUnlocks?: ProUnlocks;
  /** The quiet ways out that aren't Pro (a top-up, a free alternative). */
  links?: CareerProPitchLink[];
  /** Gets this showing's id, for the checkout to carry (see
   * experimentFunnel.ts): it is how an order is tied back to the arm. And the
   * Pro tier item they chose, for the checkout to buy. */
  onGoPro: (instanceId: string, itemId?: string) => void;
  onClose: () => void;
  /** Back to the task the pitch interrupted, where there is one to go back to. */
  onBack?: () => void;
}

/**
 * The Pro pass at every out-of-credits or locked-feature moment, and wherever
 * the job page's strip or the post-apply screen opens Pro: what they're
 * missing on Free, led by the wall they just hit (compare.tsx). The store
 * keeps its own cards for comparing plans.
 */
const CareerProPitch = ({
  placement,
  premium: proPlusItem,
  loading,
  busy,
  ctaLabel,
  note,
  lead,
  courseName,
  proUnlocks,
  links = [],
  onGoPro,
  onClose,
  onBack,
}: CareerProPitchProps) => {
  const { copy, leadTier, renderedArm, settled, preview } = useProPitchCopy();
  // Every tier on sale, Pro+ as the opener priced it. The arm's tier leads;
  // "See all Pro options" opens the rest.
  // The call-booking wall sells the call, so only a tier that waives it.
  const { options, leading } = useProTierOptions(
    proPlusItem,
    leadTier,
    lead === 'guidance',
  );
  const [chosen, setChosen] = React.useState<ProTierType>();
  const [showTiers, setShowTiers] = React.useState(false);
  const tierType = chosen ?? leading;
  const tier = proTierOf(tierType);
  // The free month is not shown past its end while the re-read is in flight.
  const premium = useLiveOffer(
    options.find((o) => o.tier.type === tierType)?.item ?? proPlusItem,
  );
  const storeUnlocks = useAppSelector((state) => state.store.proUnlocks);
  // One id per showing: every event from it, and the checkout it leads to,
  // carry it.
  const [instanceId] = React.useState(newInstanceId);
  // An admin's preview of an arm is not a showing: nothing is reported.
  const report = (eventType: FunnelEventType, detail?: string) => {
    if (preview) return;
    logProPitchEvent({
      instanceId,
      eventType,
      placement,
      lead,
      // Every showing counts in the user's arm, the call wall's and an off-sale
      // tier's too: dropping only the treatment arms' would leave control alone
      // with those (high-intent) buyers.
      variant: renderedArm,
      detail,
    });
  };
  // Reported once the user's arm is known, so the server stamps the showing
  // with the arm whose copy is on screen -- and once it is actually on screen
  // in a visible tab, the rule the Pro card counts by too: a pitch that
  // mounts after an external apply, while the candidate is still on the
  // employer's site, is not a showing until they come back.
  const shellRef = React.useRef<HTMLDivElement>(null);
  const seen = useSeen(shellRef);
  const reportedShown = React.useRef(false);
  React.useEffect(() => {
    if (!settled || !seen || reportedShown.current) return;
    reportedShown.current = true;
    // Once per showing, with the arm rendered at the moment it settled.
    report('shown');
  }, [settled, seen]);
  const proOriginal = Number(premium?.gross_price_in_currency ?? 0);
  const proPrice = Number(premium?.gross_price_in_currency_after_disc ?? 0);
  const proDiscount = Math.floor(premium?.discount_pct_for_currency ?? 0);
  const {
    isRecurring,
    freeCycles,
    freeDays,
    freePeriod,
    hasFreeTrial,
    deal,
    hasIntroOffer,
    footnote,
  } = proRecurringTerms(premium);

  const goProLabel = ctaLabel ?? 'Go Pro';
  // A "today only" free month gets a line of its own under the price, so the
  // price line says only what Pro costs -- and the countdown is plainly about
  // the free month. An always-on free month still leads the price line. A
  // first-month deal in its place takes the same spots.
  const todayOnly = hasIntroOffer && !!offerEndsAt(premium);
  const leadFree = hasFreeTrial && !todayOnly;
  const leadDealPrice = deal && !todayOnly ? deal.price : null;
  const leadDeal = leadDealPrice !== null;

  return (
    // Sized to its content on a desktop; on a phone the store dialog runs it
    // full screen, with Go Pro pinned to the bottom.
    <Shell ref={shellRef} sx={{ height: { xs: '100%', sm: 'auto' } }}>
      <CloseButton
        onClick={() => {
          report('closed');
          onClose();
        }}
        aria-label="Close"
      >
        <CloseRounded fontSize="small" />
      </CloseButton>

      <PitchBody sx={{ flex: { xs: 1, sm: '0 1 auto' } }}>
        <HeadBox>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            {onBack && (
              <IconButton
                onClick={onBack}
                size="small"
                aria-label="Back"
                sx={{ ml: -0.75 }}
              >
                <ArrowBackRounded fontSize="small" />
              </IconButton>
            )}
            <ProChip>
              <WorkspacePremiumRoundedIcon sx={{ fontSize: 13 }} />
              Abekus {tier.name}
            </ProChip>
          </Stack>
          {/* Why this opened, before anything is sold: the wall they hit, or
              what Pro makes free here. The headline then reads as the answer. */}
          {note && <ShortNote>{note}</ShortNote>}
          <PitchTitle>{copy.headline}</PitchTitle>
        </HeadBox>

        <ProCompare
          tier={tier}
          lead={lead}
          counts={{ ...storeUnlocks, ...proUnlocks }}
          // "a month" only while Pro renews: the grant comes once per cycle.
          perMonth={isRecurring ? ' a month' : ''}
          courseName={courseName}
        />
      </PitchBody>

      <CtaFooter>
        {premium && !loading ? (
          <>
            {todayOnly ? (
              <TodayOnlyPrice
                item={premium}
                freeCycles={freeCycles}
                freeDays={freeDays}
                price={proPrice}
                original={proOriginal}
                discountPct={proDiscount}
                isRecurring={!!isRecurring}
              />
            ) : (
              <PriceRow>
                <PriceMain>
                  {leadFree ? '₹0' : `₹${leadDealPrice ?? proPrice}`}
                </PriceMain>
                <PriceTerm>
                  {leadFree || leadDeal ? (
                    // The cut from the list price shows here too, not only
                    // when there is no free month to lead with.
                    <>
                      {leadDeal ? 'first month' : 'today'} · then{' '}
                      {proOriginal > proPrice && (
                        <Box
                          component="s"
                          sx={{
                            color: 'text.disabled',
                            fontWeight: 700,
                            mr: 0.5,
                          }}
                        >
                          ₹{proOriginal}
                        </Box>
                      )}
                      ₹{proPrice}/month
                    </>
                  ) : isRecurring ? (
                    '· per month, auto-renewing'
                  ) : (
                    '· 1-month access'
                  )}
                </PriceTerm>
                {!leadFree && !leadDeal && proOriginal > proPrice && (
                  <PriceStrike>₹{proOriginal}</PriceStrike>
                )}
                {leadDealPrice !== null ? (
                  proOriginal > leadDealPrice && (
                    <DiscountPill>
                      {Math.round(100 * (1 - leadDealPrice / proOriginal))}% OFF
                      1ST MONTH
                    </DiscountPill>
                  )
                ) : leadFree ? (
                  <DiscountPill>
                    {freeDays
                      ? `${freeDays} DAYS FREE`
                      : freeCycles === 1
                        ? '1ST MONTH FREE'
                        : `FIRST ${freeCycles} MONTHS FREE`}
                  </DiscountPill>
                ) : (
                  proDiscount > 0 && (
                    <DiscountPill>{proDiscount}% OFF</DiscountPill>
                  )
                )}
                {/* A time-limited discount counts down here; a "today only"
                    free month has its own line below. */}
                {!todayOnly && <OfferCountdown item={premium} />}
              </PriceRow>
            )}
            {options.length > 1 &&
              (showTiers ? (
                <Box sx={{ mb: 1.25 }}>
                  <TierPicker
                    options={options}
                    selected={tierType}
                    onSelect={(type) => {
                      setChosen(type);
                      report('link_clicked', `tier_${type}`);
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ mt: -0.5, mb: 0.75 }}>
                  <TopUpLink
                    {...ctaProps('pro_pitch_all_tiers', placement)}
                    disableRipple
                    onClick={() => {
                      setShowTiers(true);
                      report('link_clicked', 'see_all_pro_options');
                    }}
                  >
                    See all Pro options →
                  </TopUpLink>
                </Box>
              ))}
            <GoProCta
              {...ctaProps('pro_pitch_go_pro', placement, lead ?? 'default')}
              disableRipple
              disabled={busy}
              onClick={() => {
                report('go_pro', tier.type);
                onGoPro(instanceId, premium?.id);
              }}
              startIcon={!busy && <BoltRoundedIcon sx={{ fontSize: 20 }} />}
              endIcon={!busy && <ArrowForwardRounded sx={{ fontSize: 20 }} />}
            >
              {busy ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
              ) : (
                goProLabel
              )}
            </GoProCta>
            {/* One line of small print: the terms, then the legal link. */}
            <FootNote>
              {hasFreeTrial
                ? `Nothing charged for ${
                    freeCycles === 1 && !freeDays ? 'a month' : freePeriod
                  } · cancel anytime`
                : footnote}
              {` · ${PRO_AI_CREDITS_EXPIRY_NOTE}`}
              {/* On a phone the link takes its own line, with no stray dot. */}
              <Box
                component="span"
                sx={{ display: { xs: 'none', sm: 'inline' } }}
              >
                {' · '}
              </Box>
              <Typography
                component="a"
                href={`${config.LEARNING_AUTO_LOGIN_URL}/abekus-details/terms-conditions?noRedirect=true`}
                target="_blank"
                rel="noreferrer"
                sx={{ display: { xs: 'block', sm: 'inline' }, ...termsLinkSx }}
              >
                Fair use &amp; T&amp;C apply
              </Typography>
            </FootNote>
          </>
        ) : (
          <Stack sx={{ alignItems: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {links.map((link) => (
          <Box key={link.label}>
            <TopUpLink
              {...ctaProps(link.cta, placement)}
              disableRipple
              onClick={() => {
                report('link_clicked', link.cta);
                link.onClick();
              }}
            >
              {link.label}
            </TopUpLink>
          </Box>
        ))}
      </CtaFooter>
    </Shell>
  );
};

export default CareerProPitch;
